import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  BarChart3,
  Briefcase,
  MapPin,
  Megaphone,
  MessagesSquare,
  ShieldCheck,
  Target,
  Users,
} from "lucide-react";
import Reveal, { EASE } from "../components/Reveal";
import { LogoMark } from "../components/Logo";
import { useLang } from "../i18n/LanguageContext";
import Seo from "../components/Seo";

const IMG = {
  hero: "https://images.unsplash.com/photo-1777056481869-feac70afe522?q=80&w=1200&auto=format&fit=crop",
  intro: "https://images.unsplash.com/photo-1714974528737-3e6c7e4d11af?q=80&w=1400&auto=format&fit=crop",
  nursery: "https://images.unsplash.com/photo-1567746455504-cb3213f8f5b8?q=80&w=1200&auto=format&fit=crop",
};

const ICONS = {
  megaphone: <Megaphone className="h-6 w-6" />,
  briefcase: <Briefcase className="h-6 w-6" />,
  chart: <BarChart3 className="h-6 w-6" />,
  shield: <ShieldCheck className="h-5 w-5" />,
  map: <MapPin className="h-5 w-5" />,
  target: <Target className="h-5 w-5" />,
  users: <Users className="h-5 w-5" />,
  messages: <MessagesSquare className="h-5 w-5" />,
};

export default function Home() {
  const { lang, t } = useLang();
  const base = `/${lang}`;
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <>
      <Seo title={t.meta.home.title} description={t.meta.home.description} />

      {/* Hero */}
      <section ref={heroRef} className="relative overflow-hidden bg-paper-warm">
        <div className="pointer-events-none absolute -right-48 -top-48 h-[320px] w-[320px] rounded-full bg-paper-sand sm:h-[560px] sm:w-[560px]" aria-hidden="true" />
        <div className="relative z-10 mx-auto grid max-w-7xl gap-14 px-5 pb-20 pt-12 sm:px-8 lg:grid-cols-12 lg:items-center lg:gap-10 lg:pb-28 lg:pt-24">
          <div className="lg:col-span-7">
            <p className="eyebrow flex items-center gap-3">
              <span className="h-px w-8 bg-teal-600" aria-hidden="true" />
              {t.hero.eyebrow}
            </p>
            <h1 className="mt-6 font-display text-[2.55rem] font-medium leading-[1.06] tracking-tight text-navy-900 sm:text-6xl lg:text-[4.05rem]" data-testid="hero-title">
              {t.hero.titleLines.map((line, i) => (
                <span key={i} className="-mb-1 block overflow-hidden pb-1">
                  <motion.span
                    className={`block ${i === t.hero.italicLine ? "italic text-teal-700" : ""}`}
                    initial={{ y: "112%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: EASE }}
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>
            <p className="mt-7 max-w-xl text-lg leading-relaxed text-navy-900/75" data-testid="hero-subtitle">
              {t.hero.sub}
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Link to={`${base}/contact`} data-testid="hero-primary-cta" className="btn-primary">
                {t.hero.primaryCta}
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to={`${base}/services`} data-testid="hero-secondary-cta" className="btn-secondary">
                {t.hero.secondaryCta}
              </Link>
            </div>
            <p className="mt-9 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm font-medium text-navy-900/55" data-testid="hero-note">
              <MapPin className="h-4 w-4 text-teal-600" />
              {t.hero.note}
              <span className="h-1 w-1 rounded-full bg-gold" aria-hidden="true" />
              {t.hero.note2}
            </p>
          </div>

          <div className="relative lg:col-span-5">
            <motion.div style={{ y: heroY }} className="relative mx-auto max-w-[420px]">
              <div className="absolute -left-5 -top-5 h-full w-full rounded-t-[999px] rounded-b-[2rem] bg-paper-sand" aria-hidden="true" />
              <div className="relative overflow-hidden rounded-t-[999px] rounded-b-[2rem] border border-navy-900/10 shadow-[0_30px_60px_-30px_rgba(12,31,56,0.35)]">
                <img src={IMG.hero} alt={t.hero.imgAlt} className="aspect-[3/4] w-full object-cover" />
              </div>
              <div className="absolute -right-2 bottom-10 max-w-[240px] rounded-2xl border border-navy-900/10 bg-paper-warm/95 px-5 py-4 shadow-[0_20px_40px_-20px_rgba(12,31,56,0.35)] backdrop-blur sm:-right-8" data-testid="hero-markets-badge">
                <p className="flex items-center gap-2 font-heading text-[11px] font-bold uppercase tracking-[0.18em] text-navy-900/60">
                  <LogoMark className="h-5 w-5" />
                  Tonouh
                </p>
                <p className="mt-1.5 text-sm font-semibold leading-snug text-navy-900">{t.hero.badgeMarkets}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <section data-testid="marquee" className="border-y border-navy-950 bg-navy-900 py-5">
        <div className="overflow-hidden">
          <div className="flex w-max animate-marquee">
            {[0, 1].map((dup) => (
              <div key={dup} aria-hidden={dup === 1} className="flex items-center">
                {t.marquee.map((item, i) => (
                  <span key={i} className="flex items-center gap-8 pr-8">
                    <span className="h-1.5 w-1.5 rotate-45 bg-gold" aria-hidden="true" />
                    <span className="whitespace-nowrap font-heading text-sm font-semibold uppercase tracking-[0.2em] text-paper-warm/90">
                      {item}
                    </span>
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="bg-paper-warm py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="eyebrow">{t.home.intro.eyebrow}</p>
              <h2 className="mt-4 font-display text-4xl leading-[1.1] text-navy-900 md:text-5xl">{t.home.intro.title}</h2>
              <Link to={`${base}/about`} data-testid="home-intro-link" className="mt-7 inline-flex items-center gap-2 font-heading text-sm font-semibold text-teal-700 transition-colors duration-200 hover:text-teal-800">
                {t.home.intro.link}
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.08}>
              <p className="text-lg leading-relaxed text-navy-900">{t.home.intro.p1}</p>
              <p className="mt-5 text-[17px] leading-relaxed text-navy-900/70">{t.home.intro.p2}</p>
              <img src={IMG.intro} alt={t.home.intro.imgAlt} loading="lazy" className="mt-10 aspect-[16/9] w-full rounded-[2rem] border border-navy-900/10 object-cover" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="bg-paper-sand/70 py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow">{t.home.categories.eyebrow}</p>
            <h2 className="mt-4 max-w-xl font-display text-4xl leading-[1.1] text-navy-900 md:text-5xl">{t.home.categories.title}</h2>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {t.home.categories.items.map((item, i) => (
              <Reveal key={item.id} delay={i * 0.08} className="h-full">
                <Link
                  to={`${base}/services`}
                  data-testid={`home-service-card-${item.id}`}
                  className="group flex h-full flex-col rounded-3xl border border-navy-900/10 bg-paper-warm p-8 transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1.5 hover:border-teal-600/40 hover:shadow-[0_24px_50px_-24px_rgba(12,31,56,0.25)]"
                >
                  <span className="font-display text-sm italic text-navy-900/40">{item.index}</span>
                  <span className="mt-6 inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-teal-600/15 bg-teal-50 text-teal-700">
                    {ICONS[item.icon]}
                  </span>
                  <h3 className="mt-5 font-heading text-xl font-semibold text-navy-900">{item.title}</h3>
                  <p className="mt-3 flex-1 text-[15px] leading-relaxed text-navy-900/70">{item.desc}</p>
                  <span className="mt-7 inline-flex items-center gap-2 font-heading text-sm font-semibold text-teal-700">
                    {item.link}
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Specialisation */}
      <section className="bg-navy-900 py-20 text-paper-warm md:py-28">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-6">
            <Reveal>
              <p className="eyebrow-light">{t.home.specialization.eyebrow}</p>
              <h2 className="mt-4 font-display text-4xl leading-[1.1] text-paper-warm md:text-5xl">{t.home.specialization.title}</h2>
              <p className="mt-6 text-[17px] leading-relaxed text-paper-warm/70">{t.home.specialization.intro}</p>
            </Reveal>
            <ul className="mt-10 space-y-6">
              {t.home.specialization.items.map((item, i) => (
                <Reveal key={item.title} delay={0.06 * i}>
                  <li className="flex gap-4">
                    <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-paper-warm/10 bg-paper-warm/10 text-teal-100">
                      {ICONS[item.icon]}
                    </span>
                    <div>
                      <h3 className="font-heading text-lg font-semibold text-paper-warm">{item.title}</h3>
                      <p className="mt-1 text-[15px] leading-relaxed text-paper-warm/65">{item.desc}</p>
                    </div>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
          <div className="relative lg:col-span-6">
            <Reveal delay={0.1} className="lg:sticky lg:top-32">
              <div className="relative">
                <div className="absolute -right-5 -top-5 h-full w-full rounded-[2rem] border border-gold/40" aria-hidden="true" />
                <img src={IMG.nursery} alt={t.home.specialization.imgAlt} loading="lazy" className="relative aspect-[4/5] w-full rounded-[2rem] border border-paper-warm/10 object-cover" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Approach */}
      <section className="bg-paper-warm py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow">{t.home.approach.eyebrow}</p>
            <h2 className="mt-4 max-w-xl font-display text-4xl leading-[1.1] text-navy-900 md:text-5xl">{t.home.approach.title}</h2>
          </Reveal>
          <div className="mt-14 grid gap-10 sm:grid-cols-2 md:grid-cols-4 md:gap-8">
            {t.home.approach.steps.map((step, i) => (
              <Reveal key={step.k} delay={i * 0.08}>
                <div data-testid={`home-step-${step.k}`} className="relative border-t-2 border-navy-900/10 pt-7">
                  <span className="absolute -top-[5px] left-0 h-2.5 w-2.5 rounded-full bg-gold" aria-hidden="true" />
                  <span className="font-display text-4xl text-navy-900/25">{step.k}</span>
                  <h3 className="mt-3 font-heading text-lg font-semibold text-navy-900">{step.title}</h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-navy-900/70">{step.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Europe */}
      <section className="bg-paper-sand/70 py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="eyebrow">{t.home.europe.eyebrow}</p>
              <h2 className="mt-4 font-display text-4xl leading-[1.1] text-navy-900 md:text-5xl">{t.home.europe.title}</h2>
              <p className="mt-6 text-lg leading-relaxed text-navy-900">{t.home.europe.p1}</p>
              <p className="mt-4 text-[17px] leading-relaxed text-navy-900/70">{t.home.europe.p2}</p>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div data-testid="home-markets-card" className="rounded-[2rem] bg-navy-900 p-8 text-paper-warm md:p-10">
                <p className="font-heading text-xs font-bold uppercase tracking-[0.24em] text-paper-warm/50">{t.home.europe.marketsLabel}</p>
                <ul className="mt-6 space-y-4">
                  {t.home.europe.markets.map((m) => (
                    <li key={m} className="flex items-center gap-4 border-b border-paper-warm/10 pb-4 last:border-0 last:pb-0">
                      <span className="h-1.5 w-1.5 rotate-45 bg-gold" aria-hidden="true" />
                      <span className="font-display text-2xl">{m}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="bg-paper-warm py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="relative overflow-hidden rounded-[2.5rem] bg-teal-700 px-8 py-16 md:px-16 md:py-20">
            <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-teal-600/60 blur-2xl" aria-hidden="true" />
            <div className="pointer-events-none absolute -bottom-28 -left-16 h-72 w-72 rounded-full bg-navy-900/30 blur-2xl" aria-hidden="true" />
            <Reveal>
              <h2 className="max-w-2xl font-display text-4xl leading-[1.1] text-paper-warm md:text-5xl" data-testid="home-closing-title">{t.home.closing.title}</h2>
              <p className="mt-5 max-w-xl text-lg leading-relaxed text-paper-warm/85">{t.home.closing.body}</p>
              <div className="mt-9 flex flex-col gap-5 sm:flex-row sm:items-center">
                <Link to={`${base}/contact`} data-testid="home-closing-cta" className="btn-light">
                  {t.home.closing.cta}
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <a href="mailto:info@tonouhmarketing.com" data-testid="home-closing-email" className="text-sm font-semibold text-paper-warm/85 underline decoration-gold underline-offset-4 transition-colors duration-200 hover:text-paper-warm">
                  {t.home.closing.email} info@tonouhmarketing.com
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
